// Management screen for Payment Intermediaries (list + create + edit +
// activate/deactivate). Backed by /api/v1/operations/paymentIntermediaries.
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import { ConfirmModal } from '@/components/dialogs/ConfirmModal';
import type {
  PaymentIntermediary,
  PaymentIntermediaryStatusFilter,
} from '@/types/payment-intermediary';
import { PaymentIntermediaryFormDialog } from './PaymentIntermediaryFormDialog';
import {
  usePaymentIntermediariesQuery,
  useUpdatePaymentIntermediaryStatusMutation,
} from './api';

const ROWS_PER_PAGE = 10;

export function PaymentIntermediariesPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();

  const [status, setStatus] = useState<PaymentIntermediaryStatusFilter>('ALL');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  const listQuery = usePaymentIntermediariesQuery(status);
  const statusMutation = useUpdatePaymentIntermediaryStatusMutation();

  const records = listQuery.data ?? [];

  const filtered = useMemo(() => {
    if (!filter.trim()) return records;
    const term = filter.toLowerCase();
    return records.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        r.accountNumber.toLowerCase().includes(term) ||
        r.statusDescription.toLowerCase().includes(term),
    );
  }, [records, filter]);

  const paged = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const handleStatusFilter = (
    _: React.MouseEvent<HTMLElement>,
    val: PaymentIntermediaryStatusFilter | null,
  ) => {
    if (val === null) return;
    setStatus(val);
    setPage(0);
  };

  const handleCreate = async () => {
    const saved = await openDialog<PaymentIntermediary | null>((close) => (
      <PaymentIntermediaryFormDialog mode="create" onClose={close} />
    ));
    if (saved) {
      await Swal.fire({
        position: 'top-end',
        icon: 'success',
        text: t('messages.record-created-successfully', {
          defaultValue: 'Record created successfully',
        }),
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const handleEdit = async (record: PaymentIntermediary) => {
    const saved = await openDialog<PaymentIntermediary | null>((close) => (
      <PaymentIntermediaryFormDialog mode="edit" initial={record} onClose={close} />
    ));
    if (saved) {
      await Swal.fire({
        position: 'top-end',
        icon: 'success',
        text: t('messages.record-updated-successfully', {
          defaultValue: 'Record updated successfully',
        }),
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const handleToggleStatus = async (record: PaymentIntermediary) => {
    const willActivate = record.status !== 'ACTIVE';
    const confirmed = await openDialog<boolean | null>((close) => (
      <ConfirmModal
        title={
          willActivate
            ? t('pages.paymentIntermediaries.confirm.activateTitle', {
                defaultValue: 'Activate intermediary?',
              })
            : t('pages.paymentIntermediaries.confirm.deactivateTitle', {
                defaultValue: 'Deactivate intermediary?',
              })
        }
        body={
          willActivate
            ? t('pages.paymentIntermediaries.confirm.activateBody', {
                defaultValue: `Activate "${record.name}"?`,
                name: record.name,
              })
            : t('pages.paymentIntermediaries.confirm.deactivateBody', {
                defaultValue: `Deactivate "${record.name}"? It will no longer be selectable for new operations.`,
                name: record.name,
              })
        }
        onClose={close}
      />
    ));
    if (!confirmed) return;

    try {
      await statusMutation.mutateAsync({
        id: record.id,
        body: { status: willActivate ? 'ACTIVE' : 'INACTIVE' },
      });
      await Swal.fire({
        position: 'top-end',
        icon: 'success',
        text: t('messages.record-updated-successfully', {
          defaultValue: 'Record updated successfully',
        }),
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        err instanceof Error ? err.message : String(err),
        'error',
      );
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">
          {t('menus.menuPaymentIntermediaries', { defaultValue: 'Payment Intermediaries' })}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          {t('pages.paymentIntermediaries.actions.create', { defaultValue: 'New Intermediary' })}
        </Button>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <ToggleButtonGroup
              value={status}
              exclusive
              onChange={handleStatusFilter}
              size="small"
            >
              <ToggleButton value="ALL">
                {t('pages.paymentIntermediaries.filter.all', { defaultValue: 'All' })}
              </ToggleButton>
              <ToggleButton value="ACTIVE">
                {t('pages.paymentIntermediaries.status.ACTIVE', { defaultValue: 'Active' })}
              </ToggleButton>
              <ToggleButton value="INACTIVE">
                {t('pages.paymentIntermediaries.status.INACTIVE', { defaultValue: 'Inactive' })}
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(0);
              }}
              placeholder={t('pages.paymentIntermediaries.filter.placeholder', {
                defaultValue: 'Filter…',
              })}
              sx={{ minWidth: 240 }}
            />
          </Stack>

          {listQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('pages.paymentIntermediaries.table.header.name', {
                          defaultValue: 'Name',
                        })}
                      </TableCell>
                      <TableCell>
                        {t('pages.paymentIntermediaries.table.header.accountNumber', {
                          defaultValue: 'Account Number',
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t('pages.paymentIntermediaries.table.header.status', {
                          defaultValue: 'Status',
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t('pages.paymentIntermediaries.table.header.actions', {
                          defaultValue: 'Actions',
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.name}</TableCell>
                        <TableCell>{record.accountNumber}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              record.statusDescription ||
                              t(`pages.paymentIntermediaries.status.${record.status}`, {
                                defaultValue: record.status,
                              })
                            }
                            color={record.status === 'ACTIVE' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip
                              title={t('global.actions.edit', { defaultValue: 'Edit' })}
                            >
                              <IconButton size="small" onClick={() => void handleEdit(record)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                record.status === 'ACTIVE'
                                  ? t('pages.paymentIntermediaries.actions.deactivate', {
                                      defaultValue: 'Deactivate',
                                    })
                                  : t('pages.paymentIntermediaries.actions.activate', {
                                      defaultValue: 'Activate',
                                    })
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => void handleToggleStatus(record)}
                                disabled={statusMutation.isPending}
                                color={record.status === 'ACTIVE' ? 'success' : 'default'}
                              >
                                {record.status === 'ACTIVE' ? (
                                  <ToggleOnIcon fontSize="small" />
                                ) : (
                                  <ToggleOffIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {paged.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 3 }}
                          >
                            {t('messages.no-data', { defaultValue: 'No data' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
              />
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
